import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { MetallicLogo } from '../components/MetallicLogo';
import { fetchCellSWR } from '../lib/sheets';

const CTACard = ({ 
  title, 
  description, 
  linkText, 
  to, 
  testId,
  index,
  ready
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={ready ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ 
        duration: 0.4, 
        delay: 0.3 + index * 0.08,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <Link 
        to={to}
        data-testid={testId}
        className="group block bg-black/40 backdrop-blur-sm border border-white/20 rounded-lg p-5 lg:p-7 h-full
                   transition-all duration-300 ease-out
                   hover:bg-black/50 hover:border-white/30"
      >
        <div className="flex flex-col h-full min-h-[140px] lg:min-h-[200px]">
          <h3 className="font-benzin text-[18px] lg:text-[22px] text-white mb-3 lg:mb-4 tracking-tight">
            {title}
          </h3>
          <p className="text-white/40 text-[13px] lg:text-[15px] leading-relaxed mb-auto">
            {description}
          </p>
          <span className="inline-flex items-center gap-2 text-white/50 text-[13px] lg:text-[14px] mt-4 lg:mt-6
                          group-hover:text-white/70 transition-colors duration-300">
            {linkText}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const DEFAULT_EYEBROW = 'Первый в своём роде';
const DEFAULT_HEADLINE = 'Операционный\nмессенджер';
const DEFAULT_SUBLINE = 'Мы стёрли границу между коммуникацией и результатом.\nЕдиная среда, где рождаются идеи, синхронизируются команды и строится бизнес.';

const Home = () => {
  const textRef = useRef(null);
  const cardsRef = useRef(null);
  const pageRef = useRef(null);
  const [streakPos, setStreakPos] = useState({ top: 0, centerX: 0 });
  const [logoReady, setLogoReady] = useState(false);

  const handleLogoReady = useCallback(() => setLogoReady(true), []);

  useEffect(() => {
    const measure = () => {
      const textEl = textRef.current;
      const cardsEl = cardsRef.current;
      const pageEl = pageRef.current;
      if (!textEl || !cardsEl || !pageEl) return;

      const pageRect = pageEl.getBoundingClientRect();
      const textRect = textEl.getBoundingClientRect();
      const cardsRect = cardsEl.getBoundingClientRect();

      const midY = ((textRect.bottom - pageRect.top) + (cardsRect.top - pageRect.top)) / 2;
      const midX = (cardsRect.left - pageRect.left) + cardsRect.width / 2;

      setStreakPos({ top: midY, centerX: midX });
    };

    requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
    >
    <div 
      ref={pageRef}
      data-testid="home-page"
      className="min-h-screen bg-black relative overflow-hidden"
    >
      {/* Background glows */}
      <div className="absolute inset-0 z-0">
        <div
          style={{
            position: 'absolute',
            top: '-5%',
            right: '0%',
            width: '50%',
            height: '35%',
            background: 'radial-gradient(ellipse 80% 30% at 70% 40%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 35%, transparent 65%)',
            transform: 'rotate(-20deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '5%',
            right: '5%',
            width: '35%',
            height: '20%',
            background: 'radial-gradient(ellipse 85% 25% at 55% 50%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 40%, transparent 65%)',
            transform: 'rotate(-15deg)',
          }}
        />

        {streakPos.top > 0 && (
          <>
            <div
              style={{
                position: 'absolute',
                top: streakPos.top,
                left: streakPos.centerX - streakPos.centerX * 0.55,
                width: streakPos.centerX * 1.1,
                height: '1px',
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 15%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.12) 85%, transparent 100%)',
                boxShadow: '0 0 20px 2px rgba(255,255,255,0.04)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: streakPos.top - 25,
                left: streakPos.centerX - streakPos.centerX * 0.45,
                width: streakPos.centerX * 0.9,
                height: 50,
                background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(255,255,255,0.015) 0%, transparent 100%)',
              }}
            />
          </>
        )}

        <div
          style={{
            position: 'absolute',
            bottom: '-5%',
            left: '-5%',
            width: '50%',
            height: '35%',
            background: 'radial-gradient(ellipse 80% 30% at 35% 60%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 35%, transparent 65%)',
            transform: 'rotate(25deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '0%',
            right: '10%',
            width: '30%',
            height: '20%',
            background: 'radial-gradient(ellipse 75% 25% at 50% 55%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 40%, transparent 60%)',
            transform: 'rotate(15deg)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-start pt-[4vh] lg:pt-[7vh] px-6 min-h-screen">
        <div className="w-full max-w-3xl 2xl:max-w-5xl">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={logoReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="mb-[1vh] lg:mb-[1.5vh]"
          >
            <MetallicLogo 
              className="w-[280px] h-[132px] lg:w-[450px] lg:h-[212px] 2xl:w-[600px] 2xl:h-[283px] ml-[-50px] lg:ml-[-90px] 2xl:ml-[-120px] mt-[0.5vh]"
              style={{}}
              onReady={handleLogoReady}
            />
          </motion.div>
          
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={logoReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.05, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative z-20 mt-[-2vh] mb-[0.4vh]"
          >
            <p
              data-testid="eyebrow-text"
              className="font-benzin text-white/40 text-[14px] lg:text-[16px] 2xl:text-[20px] tracking-wide"
            >
              {DEFAULT_EYEBROW}
            </p>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={logoReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative z-20 mb-[1.8vh]"
          >
            <h1
              data-testid="definition-text"
              className="font-benzin text-[24px] sm:text-[30px] lg:text-[38px] 2xl:text-[52px] leading-[1.1] text-white tracking-tight whitespace-pre-line"
            >
              {DEFAULT_HEADLINE}
            </h1>
          </motion.div>
          
          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={logoReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative z-20"
          >
            <p
              ref={textRef}
              data-testid="slogan-text"
              className="text-white/55 text-[18px] leading-[1.55] max-w-[640px] mb-[5vh] lg:mb-[7vh] whitespace-pre-line"
            >
              {DEFAULT_SUBLINE}
            </p>
          </motion.div>
          
          {/* CTA Cards */}
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 w-full">
            <CTACard 
              title="Вакансии"
              description="Изучите открытые позиции и присоединяйтесь к нашей команде."
              linkText="Больше информации"
              to="/vacancies"
              testId="vacancies-cta-card"
              index={0}
              ready={logoReady}
            />
            <CTACard 
              title="Стать инвестором"
              description="Ознакомьтесь с нашим продуктом, бизнес-моделью и стратегией развития – и инвестируйте в экосистему нового поколения."
              linkText="Узнать подробнее"
              to="/investor"
              testId="investor-cta-card"
              index={1}
              ready={logoReady}
            />
          </div>
        </div>
      </div>
    </div>
    </motion.div>
  );
};

export default Home;
